import * as Styled from './WorkspaceSelect.styled';
import { usePermissions } from '@modules/auth/hooks/usePermissions/usePermissions';
import { isOwner } from '@modules/auth/utils/AuthorizationService/AuthorizationService';
import { useFlagsProxy } from '@modules/feature-flags/hooks/useFlagsProxy';
import { useCanCreateWorkspace } from '@modules/workspaces/hooks/useCanCreateWorkspace';
import WorkspaceIcon from 'app/_components/Icons/WorkspaceIcon';
import { useOrganization } from 'app/_providers/Organization';
import { useUser } from 'app/_providers/User';
import { WorkspaceWithPathName, useWorkspace } from 'app/_providers/WorkspaceProvider';

import { Pill } from '@capra/core';
import { Modal, Button, Table, Tooltip } from 'antd';
import { Route } from 'next';
import Link from 'next/link';
import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { AccountState, workspaceFailedStates, workspaceDeletingStates } from 'shared/src/account/types';
import { SystemRole } from 'shared/src/authz/rolesV2';
import { MemberAccessCheckType } from 'shared/src/generated/openapi/v3/types';

type Props = Readonly<{
  closeModal: () => unknown;
}>;

type ClassName = string | undefined;

const MAX_CHAR_TAG_DISPLAY = 12;

const WorkspaceSelect: React.FC<Props> = ({ closeModal }) => {
  const router = useRouter();
  const { cloudReleaseChannelEnabled } = useFlagsProxy();
  const { organizationAlias, organizationId } = useOrganization();
  const { name, alias, workspaces, isWorkspaceLoading } = useWorkspace();
  const user = useUser();
  const { canCreateWorkspaces, maxWorkspaces } = useCanCreateWorkspace();
  const [dataSource, setDataSource] = useState<WorkspaceWithPathName[]>([]);
  const [filteredDataSource, setFilteredDataSource] = useState<WorkspaceWithPathName[]>([]);
  const [search, setSearch] = React.useState('');
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const hasMultipleWorkspaces = workspaces.length > 1;
  const isMSSPOrganization = maxWorkspaces > 10;
  const addWorkspaceUrl = `/organizations/${organizationId}/add-workspace`;

  const userSystemPermissions = usePermissions([
    {
      accessType: MemberAccessCheckType.System,
      checks: [SystemRole.DEVELOPER, SystemRole.ADMIN],
    },
  ]);

  const isSystemDeveloper = userSystemPermissions[SystemRole.DEVELOPER];
  const isSystemAdmin = userSystemPermissions[SystemRole.ADMIN];
  const shouldShowDeveloperTools = isSystemDeveloper || isSystemAdmin || (isOwner(user) && cloudReleaseChannelEnabled);

  const getWorkspaceRedirectPath = (path: string): string => {
    if (pathname?.includes('/workspaces/')) {
      const workspaceName = path.split('/').pop();

      return pathname.replace(/workspaces\/[^/]+/, `workspaces/${workspaceName}`);
    }

    return path;
  };

  useEffect(() => {
    setDataSource(
      workspaces.map((workspace) => ({
        ...workspace,
        tags: workspace.tags || [],
      })),
    );
  }, [workspaces]);

  useEffect(() => {
    if (search) {
      setFilteredDataSource(
        dataSource.filter(
          (workspace) =>
            workspace.name.toLowerCase().includes(search.toLowerCase()) ||
            (workspace.alias?.toLowerCase() ?? '').includes(search.toLowerCase()) ||
            workspace.tags?.some((tag) => tag.toLowerCase().includes(search.toLowerCase())),
        ),
      );
    } else {
      setFilteredDataSource(dataSource);
    }
  }, [search, dataSource]);

  React.useEffect(() => {
    const searchText = searchParams?.get('search');

    if (searchText) {
      setSearch(searchText);
    } else {
      setSearch('');
    }
  }, [searchParams]);

  const onSearch =
    (value: string) => router.replace(`${pathname}?search=${encodeURIComponent(value)}` as Route);

  const workspaceColumns = [
    {
      title: 'Name',
      dataIndex: 'alias',
      key: 'alias',
      sorter: hasMultipleWorkspaces
        ? (a: WorkspaceWithPathName, b: WorkspaceWithPathName) => a.name.localeCompare(b.name)
        : undefined,
      render: (workspaceAlias: string, record: WorkspaceWithPathName): React.ReactNode => {
        if (workspaceDeletingStates.includes(record.state as AccountState)) {
          return (
            <Styled.InactiveWorkspaceInfoWrap>
              {(className: ClassName) => (
                <Styled.WorkspaceInfoText className={className}>
                  <Styled.WorkspaceIconWrap className={className}>
                    <WorkspaceIcon className={className} />
                  </Styled.WorkspaceIconWrap>

                  <Styled.WorkspaceName className={className}>{workspaceAlias || record.name}</Styled.WorkspaceName>
                </Styled.WorkspaceInfoText>
              )}
            </Styled.InactiveWorkspaceInfoWrap>
          );
        }

        return (
          <Styled.WorkspaceInfoWrap>
            {(className: ClassName) => (
              <Styled.WorkspaceInfo
                className={className}
                href={`${getWorkspaceRedirectPath(record.pathname as string)}`}
              >
                <Styled.WorkspaceIconWrap className={className}>
                  <WorkspaceIcon className={className} />
                </Styled.WorkspaceIconWrap>

                <Styled.WorkspaceName className={className}>{workspaceAlias || record.name}</Styled.WorkspaceName>
              </Styled.WorkspaceInfo>
            )}
          </Styled.WorkspaceInfoWrap>
        );
      },
    },
    {
      title: 'ID',
      dataIndex: 'name',
      key: 'name',
      sorter: hasMultipleWorkspaces
        ? (a: WorkspaceWithPathName, b: WorkspaceWithPathName) => a.name.localeCompare(b.name)
        : undefined,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      sorter: hasMultipleWorkspaces
        ? (a: WorkspaceWithPathName, b: WorkspaceWithPathName) => (a.description || '').localeCompare(b.description ?? '')
        : undefined,
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags: string[]): React.ReactNode =>
        tags?.map((tag) => (
          <Tooltip
            key={tag}
            title={tag}
          >
            <Pill inline>{tag.length > MAX_CHAR_TAG_DISPLAY ? `${tag.substring(0, MAX_CHAR_TAG_DISPLAY)}...` : tag}</Pill>
          </Tooltip>
        )),
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      render: (record: AccountState): React.ReactNode => {
        if (record === AccountState.WorkspaceActive) {
          return <Pill appearance='success' variant='outline'>Active</Pill>;
        }

        if (record === AccountState.WorkspaceAsleep) {
          return <Pill appearance='warning' variant='outline'>Hibernated</Pill>;
        }

        if (record === AccountState.WorkspaceAsleepRequested) {
          return <Pill appearance='warning' variant='muted'>Hibernating...</Pill>;
        }

        if (record === AccountState.WorkspaceWakeRequested) {
          return <Pill appearance='info' variant='muted'>Waking...</Pill>;
        }

        if (workspaceFailedStates.includes(record)) {
          return <Pill appearance='danger' variant='muted'>Failed</Pill>;
        }

        if (workspaceDeletingStates.includes(record)) {
          return (
            <Tooltip
              title='The Workspace is deleting. Deleting takes around 10 minutes.'
              mouseEnterDelay={0.5}
            >
              <Pill appearance='warning' variant='muted'>Deleting...</Pill>
            </Tooltip>
          );
        }

        return <Pill appearance='warning' variant='outline'>Provisioning</Pill>;
      },
    },
    {
      title: 'Actions',
      dataIndex: 'pathname',
      key: 'actions',
      render: (workspacePathname: string, record: WorkspaceWithPathName): React.ReactNode => (
        <Link href={{ pathname: `${workspacePathname}/edit-workspace` }}>
          <Styled.SettingsButton
            onClick={closeModal}
            disabled={workspaceDeletingStates.includes(record.state as AccountState)}
          >
            Settings
          </Styled.SettingsButton>
        </Link>
      ),
    },
  ];

  if (shouldShowDeveloperTools) {
    // inject version into the workspace columns
    workspaceColumns.splice(4, 0, {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      render: (version: string) => version || 'N/A',
    });
  }

  const currentWorkspaceClassName = 'current-workspace-row';

  return (
    <Styled.SelectWorkspaceModalWrapper>
      {(className: ClassName) => (
        <Modal
          open
          className={className}
          zIndex={1060}
          title={
            <Styled.TitleWrapper>
              <Styled.InnerWrapper>
                <div>
                  <Styled.TitleText>{organizationAlias || organizationId}</Styled.TitleText>

                  <Styled.TitleDivider />

                  <Styled.TitleText>{alias || name}</Styled.TitleText>
                </div>

                <Styled.SelectText>Select a Workspace</Styled.SelectText>
              </Styled.InnerWrapper>
            </Styled.TitleWrapper>
          }
          footer={
            <div>
              <Button
                key='ok'
                onClick={closeModal}
              >
                Close
              </Button>
            </div>
          }
          onCancel={closeModal}
          data-testid='workspace-select-modal'
        >
          <Styled.WorkspacesTableWrapper>
            <Styled.SelectText>All Workspaces in {organizationAlias || organizationId}</Styled.SelectText>

            {canCreateWorkspaces && (
              <Tooltip
                title={
                  workspaces.length >= maxWorkspaces
                    ? `The current maximum limit is ${maxWorkspaces} Workspaces for this Organization`
                    : ''
                }
                mouseEnterDelay={0.5}
              >
                <Link href={{ pathname: addWorkspaceUrl }}>
                  <Styled.AddWorkspaceButton
                    type='primary'
                    size='small'
                    disabled={workspaces.length >= maxWorkspaces}
                    onClick={closeModal}
                  >
                    Add Workspace
                  </Styled.AddWorkspaceButton>
                </Link>
              </Tooltip>
            )}
          </Styled.WorkspacesTableWrapper>

          {isMSSPOrganization && (
            <Styled.WorkspaceSearch
              placeholder='Filter Workspaces'
              defaultValue={search || ''}
              onSearch={onSearch}
              autoFocus
              enterButton='Search'
            />
          )}

          <Table
            pagination={false}
            columns={workspaceColumns}
            loading={isWorkspaceLoading}
            dataSource={filteredDataSource}
            rowKey={(record) => record.name}
            showSorterTooltip={false}
            style={{ flex: 1, overflow: 'auto' }}
            rowClassName={(record: WorkspaceWithPathName) =>
              record.name === name ? currentWorkspaceClassName : ''}
          />
        </Modal>
      )}
    </Styled.SelectWorkspaceModalWrapper>
  );
};

export default WorkspaceSelect;
